import React, { Fragment, useMemo, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { convertFromUTC } from 'utils/datetime';
import {
	buildStaffingNeedHoursByDay,
	resolveStaffingNeedEntry,
	getVisualGapHoursForDay,
	isVisualPaddingHour,
	normalizeStaffingTimeLocal,
	buildQuarterHourCoverageMap,
} from './planStaffingNeedsUtils';

// Modos de visualización de las celdas de la tabla de cobertura horaria
const COVERAGE_VIEW_MODES = {
	planned: 'planned',
	needs: 'needs',
};

/**
 * Componente mejorado para mostrar necesidades de staffing por horas con fracciones de 15 minutos
 * 
 * Características principales:
 * - Maneja correctamente fracciones de 15 minutos (4 celdas por columna)
 * - Se alinea perfectamente con las columnas de PlanTableHeader y PlanTableBody
 * - Optimizado en lógica y recursos
 * - Incluye fila de totales
 * - Compara necesidades vs planificación real
 * - CORREGIDO: Calcula correctamente turnos parciales (ej: 4:00-10:30)
 */
const PlanStaffingHourlyImproved = (props) => {
	const { t } = useTranslation();
	// Modo de visualización de las celdas: cobertura planificada o necesidades
	const [viewMode, setViewMode] = useState(COVERAGE_VIEW_MODES.planned);
	// Validación de datos más estricta para evitar renderizado prematuro
	const hasStaffingData = props.staffing_level_hourly_assignments && props.staffing_level_hourly_assignments.length > 0;
	const hasPlanAssignments = props.plan_assignments && props.plan_assignments.length > 0;
	const hasWorkingHours = props.working_hours_list && props.working_hours_list.length > 0;
	const hasPlanDays = props.plan_days && props.plan_days.length > 0;

	// Validar que todos los datos críticos estén presentes
	// Nota: hasPlanAssignments ya no es obligatorio para mostrar la tabla de necesidades
	const allDataPresent = hasStaffingData && hasWorkingHours && hasPlanDays;

	if (!allDataPresent) {
		// eslint-disable-next-line no-console
		console.log('[NEEDS] Missing data - not rendering:', { hasStaffingData, hasPlanAssignments, hasWorkingHours, hasPlanDays });
		return null;
	}

	// Memoización de datos procesados para optimización
	const processedData = useMemo(() => {
		// Validación adicional antes de procesar - plan_assignments es opcional
		if (!props.staffing_level_hourly_assignments || !props.working_hours_list) {
			return { quarterHourMap: new Map(), working_hours_list: [] };
		}

		const data = processStaffingData(props);
		const staffingNeedHoursByDay = buildStaffingNeedHoursByDay(
			props.staffing_level_hourly_assignments
		);
		return { ...data, staffingNeedHoursByDay };
	}, [props.staffing_level_hourly_assignments, props.plan_assignments, props.working_hours_list]);

	// Validación final antes del render
	// Eliminamos la validación de quarterHourMap.size > 0 porque si no hay planificados, el mapa estará vacío pero queremos ver las necesidades
	if (!processedData) {
		// eslint-disable-next-line no-console
		console.log('[NEEDS] No processed data - not rendering');
		return null;
	}


	return (
		<table id="tableNeedsImproved" className={`table table-bordered table-plan table-needs table-hourly-counter`}>
			<thead>
				<tr>
					<th className="main-col th-day">
						<div className="th-wrap">
							<div className="coverage-mode-switch" role="group">
								<button
									type="button"
									className={`coverage-mode-option${
										viewMode === COVERAGE_VIEW_MODES.planned ? ' active' : ''
									}`}
									onClick={() => setViewMode(COVERAGE_VIEW_MODES.planned)}
								>
									{t('staffingLevels.coverage')}
								</button>
								<button
									type="button"
									className={`coverage-mode-option${
										viewMode === COVERAGE_VIEW_MODES.needs ? ' active' : ''
									}`}
									onClick={() => setViewMode(COVERAGE_VIEW_MODES.needs)}
								>
									{t('staffingLevels.needs')}
								</button>
							</div>
						</div>
					</th>
					{generateStaffingHeader(props)}
				</tr>
			</thead>
			<tbody>
				{generateFunctionRows(props, processedData, viewMode)}
				{generateTotalsRow(props, processedData, viewMode)}
				{generatePlannedRow(props, processedData)}
			</tbody>
		</table>
	);
};

/**
 * Procesa los datos de staffing para optimizar el rendimiento
 * CORREGIDO: Ahora maneja correctamente los turnos parciales
 */
const processStaffingData = (props) => {
	const { plan_assignments = [], working_hours_list } = props;

	// El mapa de cobertura por cuarto de hora se construye con la utilidad
	// compartida (reutilizada también por la tabla de necesidades fraccionadas).
	const quarterHourMap = buildQuarterHourCoverageMap(plan_assignments);

	return { quarterHourMap, working_hours_list };
};

/**
 * NUEVA FUNCIÓN: Calcula qué cuartos de hora están cubiertos por un turno específico
 */
const getShiftQuarterHours = (shiftStart, shiftEnd, baseHour) => {
	const quarters = [];

	// Parsear las horas del turno
	const startTime = moment(shiftStart, 'HH:mm:ss');
	const endTime = moment(shiftEnd, 'HH:mm:ss');

	// Si el turno cruza medianoche, ajustar
	if (endTime.isBefore(startTime)) {
		endTime.add(1, 'day');
	}

	// Generar cuartos de hora para la hora base
	const baseHourPadded = baseHour.toString().padStart(2, '0');
	const quarterTimes = [
		`${baseHourPadded}:00:00Z`,
		`${baseHourPadded}:15:00Z`,
		`${baseHourPadded}:30:00Z`,
		`${baseHourPadded}:45:00Z`
	];

	quarterTimes.forEach(quarterTimeStr => {
		// Parsear el cuarto de hora
		const quarterMoment = moment(quarterTimeStr.replace('Z', ''), 'HH:mm:ss');

		// Verificar si este cuarto de hora está dentro del turno
		if (quarterMoment.isSameOrAfter(startTime) && quarterMoment.isBefore(endTime)) {
			quarters.push(quarterTimeStr);
		}
	});

	return quarters;
};

const getDayWorkingHoursList = (props, dayIndex) => {
	const perDay =
		props.plan_assignments?.[0]?.plans?.[dayIndex]?.working_hours_list;
	if (perDay?.length > 0) return perDay;
	return props.working_hours_list || [];
};

/**
 * Genera el header de la tabla con las columnas de días y horas
 */
const generateStaffingHeader = (props) => {
	return props.plan_days.map((day, dayIndex) => {
		const dayMoment = moment(day.day);
		const dayName = dayMoment.format("dd");
		const dayNum = dayMoment.format("DD");
		const isToday = dayMoment.isSame(moment(), 'day');

		// Obtener tipo de día para colores
		let dayType = null;
		if (day.details && day.details.length > 0) {
			dayType = day.details[0].special_day?.day_type || day.details[0].weekday?.day_type;
		}

		return (
			<Fragment key={dayIndex}>
				<th
					className="th-day head-hours-main transparent-header"
					style={{
						backgroundColor: '#f7f7f8',
						border: 'none',
						color: dayType?.color
					}}
				>
					{/* Sin contenido para que se vea igual a las filas de abajo */}
				</th>
				{generateHourColumns(props, dayIndex)}
			</Fragment>
		);
	});
};

/**
 * Genera las columnas de horas (una celda por hora que contiene 4 cuartos de hora)
 */
const generateHourColumns = (props, dayIndex) => {
	return getDayWorkingHoursList(props, dayIndex).map((hour, hourIndex) => {
		return (
			<th key={hourIndex} className="head-hours" title={`${hour}h - Cuartos: 00, 15, 30, 45`}>
				{hour}h
			</th>
		);
	});
};

/**
 * Genera las filas de funciones
 */
const generateFunctionRows = (props, processedData, viewMode) => {
	return props.staffing_level_hourly_assignments.map((assignment, functionIndex) => {
		return (
			<tr key={functionIndex}>
				<th>
					<div className="th-wrap">
						<span
							className="assignment-function substring"
							title={assignment.function}
						>
							{assignment.function}
						</span>
					</div>
				</th>
				{generateDayColumns(props, assignment, processedData, viewMode)}
			</tr>
		);
	});
};

/**
 * Genera las columnas de días para una función específica
 */
const generateDayColumns = (props, assignment, processedData, viewMode) => {
	// CORREGIDO: Usar plan_days en lugar de assignment.counters para asegurar todos los días
	return props.plan_days.map((planDay, dayIndex) => {
		// Buscar si existe un counter para este día específico
		const dayCounter = assignment.counters.find(counter => counter.day === planDay.day);

		// Si no existe counter para este día, crear uno vacío
		const dayData = dayCounter || {
			day: planDay.day,
			hours: []
		};

		return (
			<Fragment key={dayIndex}>
				<td className="td-day"></td>
				{generateHourCells(
					props,
					dayData,
					assignment,
					processedData,
					dayIndex,
					viewMode
				)}
			</Fragment>
		);
	});
};

/**
 * Genera las celdas de horas para un día específico (una celda por hora con 4 cuartos de hora)
 */
const generateHourCells = (
	props,
	day,
	assignment,
	processedData,
	dayIndex,
	viewMode
) => {
	const cells = [];
	const dayHours = getDayWorkingHoursList(props, dayIndex);
	const lastHour = dayHours[dayHours.length - 1];

	dayHours.forEach((hour, hourIndex) => {
		// Generar una celda por hora que contiene 4 cuartos de hora
		const quarterCells = ['00', '15', '30', '45'].map((minute, minuteIndex) => {
			const timeString = `${hour}:${minute}`;
			const cellData = calculateCellData(
				props,
				day,
				assignment,
				timeString,
				processedData,
				dayIndex
			);

			return (
				<div key={`${hourIndex}-${minuteIndex}`} className="quarter-cell">
					{renderQuarterCell(cellData, timeString, viewMode)}
				</div>
			);
		});

		// Madrugada del cierre: overflow en 23h solo si el día siguiente no abre columna 0h
		const overflowQuarters = [];
		const nextDayHours = getDayWorkingHoursList(props, dayIndex + 1);
		if (hour === lastHour && hour >= 20 && !nextDayHours.includes(0)) {
			['00', '15', '30', '45'].forEach((minute, minuteIndex) => {
				const padded = `00:${minute}`;
				const hasNeed = day.hours.some((h) => {
					const local = convertFromUTC(h.hour);
					return local === padded && Number(h.needed) > 0;
				});
				if (!hasNeed) return;

				const cellData = calculateCellData(
					props,
					day,
					assignment,
					padded,
					processedData,
					dayIndex
				);
				overflowQuarters.push(
					<div
						key={`overflow-${minuteIndex}`}
						className="quarter-cell quarter-cell-post-midnight"
					>
						{renderQuarterCell(cellData, padded, viewMode)}
					</div>
				);
			});
		}

		cells.push(
			<td
				key={hourIndex}
				className={`td-hour shift-cell${
					overflowQuarters.length ? " td-hour-post-midnight" : ""
				}`}
			>
				<div className="quarter-hour-container">
					{quarterCells}
					{overflowQuarters}
				</div>
			</td>
		);
	});

	return cells;
};

/**
 * CORREGIDA: Calcula los datos para una celda específica
 */
const calculateCellData = (props, day, assignment, timeString, processedData, dayIndex) => {
	const { quarterHourMap, staffingNeedHoursByDay } = processedData;
	const dayKey = day.day;
	const normalizedTime = normalizeStaffingTimeLocal(timeString);
	const hourNum = parseInt(normalizedTime.split(":")[0], 10);
	const visualGapHours = getVisualGapHoursForDay(props.plan_assignments, dayIndex);

	if (
		isVisualPaddingHour(
			visualGapHours,
			staffingNeedHoursByDay,
			dayKey,
			hourNum
		)
	) {
		return {
			needed: 0,
			actual: 0,
			assignedPeople: [],
			status: "exact",
		};
	}

	const needEntry = resolveStaffingNeedEntry({
		day,
		dayIndex,
		planDays: props.plan_days,
		timeString,
		functionCounters: assignment.counters,
	});

	const needed = needEntry ? Number(needEntry.needed) || 0 : 0;

	// CORREGIDO: Buscar planificación real por cuarto de hora específico
	const functionName = assignment.function;

	// Formatear el tiempo como lo espera el mapa (agregar segundos y Z) y normalizar HH
	const [hhRaw, mmRaw] = normalizedTime.split(":");
	const quarterHourKey = `${hhRaw}:${mmRaw}:00Z`;
	const mapKey = `${dayKey}_${functionName}_${quarterHourKey}`;

	let actualData = quarterHourMap.get(mapKey);
	if (!actualData) {
		// CORREGIDO: No usar fallback a ALL, solo contar empleados de la función específica
		actualData = { peopleSet: new Set(), people: [] };
	}
	const assignedPeople = actualData.people || [];
	const actualCount = assignedPeople.length;
	// Si la necesidad exacta para este cuarto no existe (needed=0) pero sí existía para el cuarto anterior,
	// evitamos arrastrar presencia más allá del fin real del turno usando SOLO cobertura real por cuartos (ya aplicada arriba).

	// Redondear a 2 decimales
	const roundedActual = Math.round(actualCount * 100) / 100;

	const result = {
		needed,
		actual: roundedActual,
		assignedPeople,
		status: getCellStatus(needed, roundedActual)
	};

	// Reducir ruido: no loguear por celda

	return result;
};

/**
 * Determina el estado de una celda (sobre, bajo, exacto, vacío)
 */
const getCellStatus = (needed, actual) => {
	if (actual > needed) return 'over';
	if (actual < needed) return 'under';
	return 'exact';
};

/**
 * Renderiza una celda de cuarto de hora
 */
const renderQuarterCell = (cellData, timeString, viewMode) => {
	const { needed, actual, assignedPeople, status } = cellData;
	const showNeeds = viewMode === COVERAGE_VIEW_MODES.needs;

	// No mostrar nada si no hay necesidad y no hay personal asignado
	if (needed === 0 && actual === 0) {
		return null;
	}

	const tooltipContent = (
		<Tooltip className="tooltip-staffing">
			<div>
				<strong>Necesario:</strong> {needed}<br />
				<strong>Planificado:</strong> {actual}<br />
				<strong>Estado:</strong> {
					status === 'over' ? 'Sobrepersonal' :
						status === 'under' ? 'Falta personal' : 'Exacto'
				}
				{assignedPeople.length > 0 && (
					<>
						<br />
						<strong>{assignedPeople.length === 1 ? 'Empleado planificado:' : 'Empleados planificados:'}</strong><br />
						{assignedPeople.map((person, index) => (
							<span key={index}>
								{person}
								{index < assignedPeople.length - 1 && <br />}
							</span>
						))}
					</>
				)}
			</div>
		</Tooltip>
	);

	const cellClass = `quarter-hour-cell ${status}${showNeeds ? ' needs-mode' : ''}`;

	return (
		<OverlayTrigger placement="top" overlay={tooltipContent}>
			<div className={cellClass}>
				{showNeeds ? needed : actual}
			</div>
		</OverlayTrigger>
	);
};

/**
 * Genera la fila de totales
 */
const generateTotalsRow = (props, processedData, viewMode) => {
	const totalsData = calculateTotals(props, processedData);

	return (
		<tr className="totals-row">
			<th>
				<div className="th-wrap" style={{ fontWeight: 'bold' }}>
					Totales
				</div>
			</th>
			{generateTotalsCells(props, totalsData, viewMode)}
		</tr>
	);
};

/**
 * Genera la fila de planificados
 */
const generatePlannedRow = (props, processedData) => {
	const plannedData = calculatePlanned(props, processedData);

	return (
		<tr className="planned-row">
			<th>
				<div className="th-wrap">
					Empleados planificados
				</div>
			</th>
			{generatePlannedCells(props, plannedData)}
		</tr>
	);
};

/**
 * Calcula los totales para todas las funciones
 */
const calculateTotals = (props, processedData) => {
	const totals = new Map();

	props.staffing_level_hourly_assignments.forEach(assignment => {
		assignment.counters.forEach(day => {
			const dayKey = day.day;
			const dayIndex = props.plan_days.findIndex((d) => d.day === dayKey);
			const dayHours = getDayWorkingHoursList(
				props,
				dayIndex >= 0 ? dayIndex : 0
			);

			dayHours.forEach(hour => {
				['00', '15', '30', '45'].forEach(minute => {
					const timeString = `${hour}:${minute}`;
					const cellKey = `${dayKey}_${timeString}`;

					if (!totals.has(cellKey)) {
						totals.set(cellKey, { needed: 0, actual: 0, assignedPeople: [] });
					}

					const cellData = calculateCellData(
						props,
						day,
						assignment,
						timeString,
						processedData,
						dayIndex >= 0 ? dayIndex : 0
					);
					const total = totals.get(cellKey);

					total.needed += cellData.needed;
					total.actual += cellData.actual;

					// Agregar personas asignadas (evitar duplicados)
					if (cellData.assignedPeople && Array.isArray(cellData.assignedPeople)) {
						cellData.assignedPeople.forEach(person => {
							if (!total.assignedPeople.includes(person)) {
								total.assignedPeople.push(person);
							}
						});
					}
				});
			});
		});
	});

	return totals;
};

/**
 * Calcula solo los empleados planificados (asignados) para todas las funciones
 * CORREGIDO: Ahora usa la clave "ALL" para contar TODOS los empleados planificados,
 * no solo los de las funciones que están en las necesidades
 */
const calculatePlanned = (props, processedData) => {
	const { quarterHourMap } = processedData;
	const planned = new Map();

	// Iterar sobre todos los días de la planificación
	props.plan_days.forEach((planDay, dayIndex) => {
		const dayKey = planDay.day;
		const dayHours = getDayWorkingHoursList(props, dayIndex);

		dayHours.forEach(hour => {
			['00', '15', '30', '45'].forEach(minute => {
				const timeString = `${hour}:${minute}`;
				const cellKey = `${dayKey}_${timeString}`;

				// Formatear el tiempo como lo espera el mapa (agregar segundos y Z) y normalizar HH
				const hh = hour.toString().padStart(2, '0');
				const quarterHourKey = `${hh}:${minute}:00Z`;

				// CORREGIDO: Usar la clave "ALL" para obtener TODOS los empleados planificados
				const allKey = `${dayKey}_ALL_${quarterHourKey}`;
				const allData = quarterHourMap.get(allKey);

				if (allData && allData.peopleSet.size > 0) {
					planned.set(cellKey, {
						actual: allData.peopleSet.size,
						assignedPeople: allData.people || []
					});
				}
			});
		});
	});

	return planned;
};

/**
 * Genera las celdas de totales
 */
const generateTotalsCells = (props, totalsData, viewMode) => {
	const cells = [];

	// CORREGIDO: Usar plan_days para asegurar todos los días se renderizan
	props.plan_days.forEach((planDay, dayIndex) => {
		cells.push(<td key={`day-${dayIndex}`} className="td-day"></td>);

		getDayWorkingHoursList(props, dayIndex).forEach((hour, hourIndex) => {
			// Generar una celda por hora que contiene 4 cuartos de hora
			const quarterCells = ['00', '15', '30', '45'].map((minute, minuteIndex) => {
				const timeString = `${hour}:${minute}`;
				const cellKey = `${planDay.day}_${timeString}`;
				const total = totalsData.get(cellKey) || { needed: 0, actual: 0 };

				return (
					<div key={`${dayIndex}-${hourIndex}-${minuteIndex}`} className="quarter-cell">
						{renderTotalsCell(total, timeString, viewMode)}
					</div>
				);
			});

			cells.push(
				<td key={`${dayIndex}-${hourIndex}`} className="td-hour shift-cell totals-cell">
					<div className="quarter-hour-container">
						{quarterCells}
					</div>
				</td>
			);
		});
	});

	return cells;
};

/**
 * Genera las celdas de planificados
 */
const generatePlannedCells = (props, plannedData) => {
	const cells = [];

	// CORREGIDO: Usar plan_days para asegurar todos los días se renderizan
	props.plan_days.forEach((planDay, dayIndex) => {
		cells.push(<td key={`day-${dayIndex}`} className="td-day"></td>);

		getDayWorkingHoursList(props, dayIndex).forEach((hour, hourIndex) => {
			// Generar una celda por hora que contiene 4 cuartos de hora
			const quarterCells = ['00', '15', '30', '45'].map((minute, minuteIndex) => {
				const timeString = `${hour}:${minute}`;
				const cellKey = `${planDay.day}_${timeString}`;
				const planned = plannedData.get(cellKey) || { actual: 0 };

				return (
					<div key={`${dayIndex}-${hourIndex}-${minuteIndex}`} className="quarter-cell">
						{renderPlannedCell(planned, timeString)}
					</div>
				);
			});

			cells.push(
				<td key={`${dayIndex}-${hourIndex}`} className="td-hour shift-cell planned-cell">
					<div className="quarter-hour-container">
						{quarterCells}
					</div>
				</td>
			);
		});
	});

	return cells;
};

/**
 * Renderiza una celda de totales
 */
const renderTotalsCell = (total, timeString, viewMode) => {
	const { needed, actual, assignedPeople } = total;
	const showNeeds = viewMode === COVERAGE_VIEW_MODES.needs;

	if (needed === 0 && actual === 0) {
		return null;
	}

	const status = getCellStatus(needed, actual);

	const tooltipContent = (
		<Tooltip className="tooltip-staffing">
			<div>
				<strong>Total Necesario:</strong> {needed}<br />
				<strong>Total Planificado:</strong> {actual}<br />
				<strong>Estado:</strong> {
					status === 'over' ? 'Sobrepersonal' :
						status === 'under' ? 'Falta personal' : 'Exacto'
				}
				{assignedPeople && assignedPeople.length > 0 && (
					<>
						<br />
						<strong>{assignedPeople.length === 1 ? 'Empleado planificado:' : 'Empleados planificados:'}</strong><br />
						{assignedPeople.map((person, index) => (
							<span key={index}>
								{person}
								{index < assignedPeople.length - 1 && <br />}
							</span>
						))}
					</>
				)}
			</div>
		</Tooltip>
	);

	return (
		<OverlayTrigger placement="top" overlay={tooltipContent}>
			<div
				className={`quarter-hour-cell totals ${status}${
					showNeeds ? ' needs-mode' : ''
				}`}
			>
				{showNeeds ? needed : actual}
			</div>
		</OverlayTrigger>
	);
};

/**
 * Renderiza una celda de planificados
 */
const renderPlannedCell = (planned, timeString) => {
	const { actual, assignedPeople } = planned;

	if (actual === 0) {
		return null;
	}

	const tooltipContent = (
		<Tooltip className="tooltip-staffing">
			<div>
				<strong>Planificado:</strong> {actual}<br />
				{assignedPeople && assignedPeople.length > 0 && (
					<>
						<strong>{assignedPeople.length === 1 ? 'Empleado planificado:' : 'Empleados planificados:'}</strong><br />
						{assignedPeople.map((person, index) => (
							<span key={index}>
								{person}
								{index < assignedPeople.length - 1 && <br />}
							</span>
						))}
					</>
				)}
			</div>
		</Tooltip>
	);

	return (
		<OverlayTrigger placement="top" overlay={tooltipContent}>
			<div className="quarter-hour-cell planned">
				{actual}
			</div>
		</OverlayTrigger>
	);
};

export default PlanStaffingHourlyImproved;